import React, { useState, useEffect } from 'react';
import { FaUsers, FaArrowUp, FaArrowDown, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import API_URL from '../config';
import DrillDown from '../DrillDown';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#4F46E5', '#EC4899', '#10B981', '#F59E0B'];

function Population() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('growth');
  const [drillDown, setDrillDown] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/population`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success') {
          setData(result.data.sort((a, b) => a.year - b.year));
        } else {
          setError(result.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to connect to API');
        setLoading(false);
      });
  }, []);

  const latest = data.length > 0 ? data[data.length - 1] : null;
  const previous = data.length > 1 ? data[data.length - 2] : null;

  const genderData = latest ? [
    { name: 'Male', value: latest.male_population || 49.5 },
    { name: 'Female', value: latest.female_population || 50.5 },
  ] : [];

  const METRICS = [
    {
      title: "Total Population",
      value: `${latest?.total_population}M`,
      change: previous ? `${((latest?.total_population - previous?.total_population) / previous?.total_population * 100).toFixed(1)}%` : null,
      isPositive: true,
      subtitle: `${latest?.year} estimate`,
      metric: {
        key: 'total_population',
        label: 'Total Population',
        color: '#4F46E5',
        unit: 'M',
        higherIsBetter: true,
        description: 'Total population of Kenya in millions',
        info: 'Kenya population has been growing steadily at about 2.3% per year, making it one of the fastest growing populations in East Africa.'
      }
    },
    {
      title: "Growth Rate",
      value: `${latest?.growth_rate}%`,
      change: "annually",
      isPositive: true,
      subtitle: "Per year",
      metric: {
        key: 'growth_rate',
        label: 'Population Growth Rate',
        color: '#10B981',
        unit: '%',
        higherIsBetter: null,
        description: 'Annual population growth rate as a percentage',
        info: 'Kenya\'s population growth rate has been gradually declining from over 3% in the 1980s to around 2.3% today, reflecting improvements in education and family planning.'
      }
    },
    {
      title: "Urban Population",
      value: `${latest?.urban_population?.toFixed(1)}M`,
      change: "+1.2%",
      isPositive: true,
      subtitle: "Of total",
      metric: {
        key: 'urban_population',
        label: 'Urban Population',
        color: '#8B5CF6',
        unit: 'M',
        higherIsBetter: true,
        description: 'Number of people living in urban areas in millions',
        info: 'Kenya is urbanizing rapidly with Nairobi and Mombasa being the largest urban centers. Urban population is expected to surpass rural by 2050.'
      }
    },
    {
      title: "Rural Population",
      value: `${latest?.rural_population?.toFixed(1)}M`,
      change: "-0.8%",
      isPositive: false,
      subtitle: "Of total",
      metric: {
        key: 'rural_population',
        label: 'Rural Population',
        color: '#F59E0B',
        unit: 'M',
        higherIsBetter: null,
        description: 'Number of people living in rural areas in millions',
        info: 'Rural population is gradually declining as more Kenyans migrate to urban areas in search of employment and better opportunities.'
      }
    }
  ];

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <FaSpinner className="text-5xl text-indigo-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading population data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-lg shadow-md">
        <p className="text-red-600 text-xl font-semibold mb-2">Error loading data</p>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center mb-4">
              <FaUsers className="text-4xl mr-4" />
              <div>
                <h1 className="text-4xl font-bold">Population Data</h1>
                <p className="text-indigo-200 mt-1">Kenya Demographics & Growth Trends</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Key Metrics */}
        {latest && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {METRICS.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MetricCard
                  {...item}
                  onClick={() => setDrillDown(item.metric)}
                />
              </motion.div>
            ))}
          </div>
        )}

        {/* Chart Tabs */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Population Trends</h3>
            <div className="flex gap-2">
              {[
                { key: 'growth', label: 'Growth' },
                { key: 'urban', label: 'Urban vs Rural' },
                { key: 'gender', label: 'Gender' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveChart(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeChart === tab.key
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Population Growth Line Chart */}
          {activeChart === 'growth' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}M`, '']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total_population"
                    stroke="#4F46E5"
                    strokeWidth={3}
                    dot={{ fill: '#4F46E5', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Total Population (M)"
                  />
                  <Line
                    type="monotone"
                    dataKey="female_population"
                    stroke="#EC4899"
                    strokeWidth={2}
                    dot={{ fill: '#EC4899', r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Female (M)"
                  />
                  <Line
                    type="monotone"
                    dataKey="male_population"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ fill: '#10B981', r: 4 }}
                    activeDot={{ r: 7 }}
                    name="Male (M)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Urban vs Rural Bar Chart */}
          {activeChart === 'urban' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value?.toFixed(2)}M`, '']}
                  />
                  <Legend />
                  <Bar dataKey="urban_population" fill="#4F46E5" name="Urban (M)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="rural_population" fill="#10B981" name="Rural (M)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Gender Pie Chart */}
          {activeChart === 'gender' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center"
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    outerRadius={130}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                    labelLine={true}
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}%`, '']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </div>

        {/* Historical Data Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Historical Data</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Year</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Total (M)</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Urban (M)</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Rural (M)</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Growth %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border-t hover:bg-indigo-50 transition cursor-pointer"
                  >
                    <td className="px-4 py-3 font-semibold text-indigo-600">{row.year}</td>
                    <td className="px-4 py-3">{row.total_population}</td>
                    <td className="px-4 py-3">{row.urban_population?.toFixed(2)}</td>
                    <td className="px-4 py-3">{row.rural_population?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className="text-green-600 font-medium">+{row.growth_rate}%</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{row.source}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Data Source */}
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-gray-700">
            <strong>Data Sources:</strong> Kenya National Bureau of Statistics (KNBS),
            World Bank Open Data, 2024 Population Projections
          </p>
        </div>
      </div>

      {/* DrillDown Panel */}
      <DrillDown
        isOpen={drillDown !== null}
        onClose={() => setDrillDown(null)}
        metric={drillDown}
        data={data}
      />
    </div>
  );
}

function MetricCard({ title, value, change, isPositive, subtitle, onClick }) {
  return (
    <div
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition cursor-pointer hover:scale-105 transform"
      onClick={onClick}
    >
      <p className="text-gray-600 text-sm mb-2">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
      <div className="flex items-center">
        {isPositive !== null && (
          isPositive ?
            <FaArrowUp className="text-green-600 mr-1" /> :
            <FaArrowDown className="text-red-600 mr-1" />
        )}
        <p className={`text-sm ${isPositive === true ? 'text-green-600' : isPositive === false ? 'text-red-600' : 'text-gray-600'}`}>
          {change}
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      <p className="text-xs text-indigo-500 mt-2 font-medium">Click for details →</p>
    </div>
  );
}

export default Population;